import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { AddOutlined as AddIcon, WarningAmber as WarningIcon } from '@mui/icons-material';

import { FBoxContainer, FHButton, FProgressBar, FHTableContainer } from '../../../../base';
import { parentSupervisionActions } from '../../../../../store';
import { useParentSupervisionEditable } from '../../../../../hooks';
import ParentGoalsRow from './ParentGoalsRow';
import {
  boxContainerHeaderStyle,
  boxContainerStyle,
  contentStyle,
  goalsProgressHeaderConfig,
} from '../constants';
import { FORTA_BLUE, FORTA_ERROR } from '../../../../../utils/colors';

import type {
  IParentGoalProgress,
  IParentSupervision,
  ISelectOption,
  ITreatmentPlanGoal,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

interface ParentGoalsProgressProps {
  parentSupervision: IParentSupervision | null;
}

function ParentGoalsProgress(props: ParentGoalsProgressProps) {
  const { parentSupervision } = props;
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const isEditable = useParentSupervisionEditable();
  const { caregiverGoals, loading: goalsLoading } = useSelector((state: RootState) => state.goals);
  const [noCaregiverGoals, setNoCaregiverGoals] = useState<boolean>(false);
  const [goalOptions, setGoalOptions] = useState<Array<ISelectOption & { value: number }>>([]);
  const [addedGoals, setAddedGoals] = useState<Array<number>>([]);
  const [goalProgresses, setGoalProgresses] = useState<IParentGoalProgress[]>([]);

  useEffect(() => {
    setGoalProgresses(parentSupervision?.goal_progress ?? []);
    updateAddedGoals(parentSupervision?.goal_progress);
  }, [parentSupervision?.goal_progress]);

  useEffect(() => {
    if (!caregiverGoals || !parentSupervision) {
      return;
    }

    const idsOfSavedGoals =
      parentSupervision.goal_progress?.map((progress: IParentGoalProgress) => progress.goal_id) ||
      [];
    const goals = caregiverGoals.filter(
      (caregiverGoal): caregiverGoal is ITreatmentPlanGoal & { id: number } =>
        caregiverGoal.id !== undefined &&
        (caregiverGoal.is_active || idsOfSavedGoals.includes(caregiverGoal.id)),
    );
    if (!goals?.length) {
      setNoCaregiverGoals(true);
      return;
    }
    setNoCaregiverGoals(false);
    const options = goals.map((goal) => ({
      value: goal.id,
      label: goal.goal_name?.label ?? '',
    }));
    setGoalOptions(options);
  }, [caregiverGoals, parentSupervision]);

  const updateAddedGoals = (parentGoals: Array<IParentGoalProgress> | undefined) => {
    const added = parentGoals?.map((goal) => goal.goal_id) ?? [];
    setAddedGoals(added);
  };

  const handleUpdateGoalRow = (prevId: number, id: number) => {
    const added = [...addedGoals];
    const index = added.indexOf(prevId);
    if (index !== -1) {
      added[index] = id;
      const updatedGoals = goalProgresses.map((progress: IParentGoalProgress) => {
        if (progress.goal_id === prevId) {
          return {
            ...progress,
            goal_id: id,
          };
        }
        return progress;
      });
      dispatch(parentSupervisionActions.addParentSupervisionGoalProgress(updatedGoals));
      setAddedGoals(added);
    }
  };

  const handleAddAllGoals = () => {
    const goalsToAdd: Array<IParentGoalProgress> = [];
    goalOptions.forEach((goal) => {
      if (!addedGoals.includes(goal.value)) {
        const data = {
          goal_id: goal.value,
          goal_evaluation: '',
        };
        goalsToAdd.push(data);
      }
    });
    const updatedGoals = [...goalProgresses, ...goalsToAdd];
    dispatch(parentSupervisionActions.addParentSupervisionGoalProgress(updatedGoals));
  };

  const handleAddGoal = () => {
    const filteredGoals = goalOptions.filter((goal) => !addedGoals.includes(goal.value));
    const goal = filteredGoals[0];
    if (!goal) return;
    const data = {
      goal_id: goal.value,
      goal_evaluation: '',
    };
    dispatch(parentSupervisionActions.addParentSupervisionGoalProgress([...goalProgresses, data]));
  };

  const handleDeleteGoalRow = (progress: IParentGoalProgress) => {
    if (!progress.id) {
      const data = goalProgresses?.filter(
        (goal: IParentGoalProgress) => goal.goal_id !== progress.goal_id,
      );
      dispatch(parentSupervisionActions.addParentSupervisionGoalProgress(data));
    } else {
      dispatch(
        parentSupervisionActions.deleteParentSupervisionGoalProgress({
          getAccessTokenSilently,
          parentSupervisionId: parentSupervision?.id,
          goalProgressId: progress.id,
        }),
      );
    }
  };

  return (
    <FBoxContainer
      sx={boxContainerStyle}
      headerStyle={boxContainerHeaderStyle}
      contentStyle={contentStyle}
    >
      {goalsLoading && <FProgressBar />}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography fontSize={21} textAlign="left" mt={1} mb={2} color={FORTA_BLUE.primary}>
          Goals progress
        </Typography>
        {isEditable && (
          <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
            <FHButton
              data-testid="addAllGoalsButton"
              sx={{ height: 40, mr: 3 }}
              startIcon={<AddIcon />}
              disabled={!isEditable}
              onClick={handleAddAllGoals}
            >
              Add all goals
            </FHButton>
            <FHButton
              data-testid="addGoalButtonParentGoal"
              sx={{ height: 40, mr: 3 }}
              startIcon={<AddIcon />}
              disabled={!isEditable}
              onClick={handleAddGoal}
            >
              Add goal
            </FHButton>
          </Box>
        )}
      </Box>
      {parentSupervision != null &&
        Object.hasOwn(parentSupervision, 'id') &&
        noCaregiverGoals &&
        !goalsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
            <WarningIcon sx={{ color: FORTA_ERROR }} />
            <Typography ml={1}>At least one active parent/caregiver goal is required</Typography>
          </Box>
        )}
      {goalProgresses.length > 0 && (
        <FHTableContainer
          sx={{ flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
          headerStyle={{
            height: 64,
            paddingLeft: 1.5,
          }}
          headerConfig={goalsProgressHeaderConfig}
        >
          {goalProgresses.map((progress: IParentGoalProgress, index: number) => (
            <ParentGoalsRow
              data-testid="parentsGoalRow"
              key={progress.goal_id}
              index={index}
              isEditable={isEditable}
              goalProgress={progress}
              goalOptions={goalOptions}
              addedGoals={addedGoals}
              onChange={handleUpdateGoalRow}
              onDelete={handleDeleteGoalRow}
            />
          ))}
        </FHTableContainer>
      )}
    </FBoxContainer>
  );
}

export default ParentGoalsProgress;
